import { GfMerchant, PointDto } from "@swagger/typescript-fetch-goflyer";
import {
  StandardizedOnboardingData,
  syncOpeningHoursData,
} from "./onboardingDataTransformer";
import { ensureOpeningHoursArray } from "./storeDetailsHelpers";

export type FieldValue =
  | string
  | number
  | Array<any>
  | { coordinates: number[] }
  | null
  | undefined;

export type FieldPath =
  | "storeTag"
  | "merchantName"
  | "merchantNameChinese"
  | "website"
  | "signature"
  | "phone"
  | "googleMapUrl"
  | "address"
  | "city"
  | "province"
  | "country"
  | "zip"
  | "location"
  | `Hours_${number}_${"open" | "close"}`;

const STORE_FIELD_MAP: Record<string, keyof any> = {
  signature: "signature",
  phone: "phone",
  googleMapUrl: "googleMapUrl",
  address: "address",
  city: "city",
  province: "province",
  country: "country",
  zip: "zip",
  storeTag: "storeTag",
} as const;

const MERCHANT_FIELD_MAP: Record<string, keyof GfMerchant> = {
  merchantName: "name",
  merchantNameChinese: "nameChinese",
  website: "domain",
} as const;

export const updateStoreField = (
  prev: StandardizedOnboardingData,
  field: string,
  value: FieldValue
): StandardizedOnboardingData => {
  const storeField = STORE_FIELD_MAP[field];
  if (storeField) {
    return {
      ...prev,
      store: {
        ...prev.store,
        [storeField]: value,
      },
    };
  }
  return prev;
};

export const updateMerchantField = (
  prev: StandardizedOnboardingData,
  field: string,
  value: FieldValue
): StandardizedOnboardingData => {
  const merchantField = MERCHANT_FIELD_MAP[field];
  if (merchantField) {
    return {
      ...prev,
      merchant: {
        ...(prev?.merchant || {}),
        [merchantField]: value,
      } as GfMerchant,
    };
  }
  return prev;
};

export const updateLocationField = (
  prev: StandardizedOnboardingData,
  value: PointDto
): StandardizedOnboardingData => {
  return {
    ...prev,
    store: {
      ...prev.store,
      location: value,
    },
  };
};

export const updateOpeningHoursField = (
  prev: StandardizedOnboardingData,
  field: string,
  value: string
): StandardizedOnboardingData => {
  const [, dayIdxStr, type] = field.split("_");
  const dayIdx = parseInt(dayIdxStr);

  if (isNaN(dayIdx) || !["open", "close"].includes(type)) {
    console.warn(`Invalid opening hours field: ${field}`);
    return prev;
  }

  const currentHours = ensureOpeningHoursArray(prev?.store.openingHours || []);
  const dayHours = currentHours[dayIdx] || {
    open: { day: dayIdx, time: "0900" },
    close: { day: dayIdx, time: "1700" },
  };

  if (type === "open") {
    dayHours.open = { day: dayIdx, time: value || "0900" };
  } else if (type === "close") {
    dayHours.close = { day: dayIdx, time: value || "1700" };
  }

  currentHours[dayIdx] = dayHours;

  const updatedStore = {
    ...prev.store,
    openingHours: currentHours,
  };

  const syncedStore = syncOpeningHoursData(updatedStore, "openingHours");

  return {
    ...prev,
    store: syncedStore,
  };
};

export const createFieldChangeHandler = () => {
  return (field: FieldPath, value: FieldValue) => {
    return (
      prev: StandardizedOnboardingData | null
    ): StandardizedOnboardingData | null => {
      if (!prev) return prev;

      // Handle operating hours fields
      if (field.includes("Hours_")) {
        return updateOpeningHoursField(prev, field, value as string);
      }

      // Handle location field
      if (field === "location") {
        return updateLocationField(prev, value as PointDto);
      }

      // Handle merchant fields
      const merchantUpdate = updateMerchantField(prev, field, value);
      if (merchantUpdate !== prev) {
        return merchantUpdate;
      }

      // Handle store fields
      const storeUpdate = updateStoreField(prev, field, value);
      if (storeUpdate !== prev) {
        return storeUpdate;
      }

      console.warn(`Unhandled field change: ${field}`, value);
      return prev;
    };
  };
};
